import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import PageHero from '../components/ui/PageHero';
import Reveal from '../components/ui/Reveal';
import { plans } from '../data/pricing';
import './Pricing.css';

export default function Pricing() {
  const [market, setMarket] = useState('in');
  const [billing, setBilling] = useState('annual');

  const isIn = market === 'in';
  const sym = isIn ? '₹' : '$';

  return (
    <div className="pricing-page">
      <PageHero
        eyebrow="Pricing"
        title="Plans built for how you actually work."
        body="Choose the tier that fits your team today — every plan bundles appointments, workforce management, and customer tools, and you can switch between monthly and annual billing whenever it suits you."
      />

      <section className="section">
        <div className="container">
          <Reveal className="section-head center">
            <span className="eyebrow">Subscription Plans</span>
            <h2>Pick your growth stage.</h2>
            <p>Every plan includes a base set of credits and business units — scale up as you grow.</p>
          </Reveal>

          <Reveal className="pricing-toggles">
            <div className="mode-toggle pricing-market-toggle">
              <button
                type="button"
                className={`mode-chip ${isIn ? 'active' : ''}`}
                onClick={() => setMarket('in')}
              >
                <strong>India</strong>
                <span>₹ INR pricing</span>
              </button>
              <button
                type="button"
                className={`mode-chip ${!isIn ? 'active' : ''}`}
                onClick={() => setMarket('us')}
              >
                <strong>USA</strong>
                <span>$ USD pricing</span>
              </button>
            </div>

            <div className="mode-toggle pricing-billing-toggle">
              <button
                type="button"
                className={`mode-chip ${billing === 'monthly' ? 'active' : ''}`}
                onClick={() => setBilling('monthly')}
              >
                <strong>Monthly</strong>
                <span>Billed every month</span>
              </button>
              <button
                type="button"
                className={`mode-chip ${billing === 'annual' ? 'active' : ''}`}
                onClick={() => setBilling('annual')}
              >
                <strong>Annually</strong>
                <span className="pricing-save-badge">Save up to 15%</span>
              </button>
            </div>
          </Reveal>

          <Reveal as="div" stagger className="pricing-grid">
            {plans.map((plan) => {
              const priceSet = isIn ? plan.priceIn : plan.priceUs;
              const currentPrice = plan.isTrial ? priceSet.monthly : priceSet[billing];
              const cycleLabel = plan.isTrial ? 'for 14 days' : billing === 'annual' ? 'per year' : 'per month';
              const monthlyTotal = priceSet.monthly * 12;
              const savingsPct = !plan.isTrial && billing === 'annual' && monthlyTotal > 0
                ? Math.round(((monthlyTotal - priceSet.annual) / monthlyTotal) * 100)
                : 0;
              const extraBu = isIn ? plan.extraBu.in : plan.extraBu.us;

              return (
                <div
                  key={plan.id}
                  className={`pricing-card card reveal-item ${plan.featured ? 'pricing-card-featured' : ''} ${plan.isTrial ? 'pricing-card-trial' : ''}`}
                >
                  {plan.featured && <span className="pricing-badge">Most Popular</span>}

                  <div className="pricing-card-head">
                    <span className="pricing-card-sub">{plan.sub} · {plan.segment}</span>
                    <h3>{plan.name}</h3>
                    <p>{plan.tagline}</p>
                  </div>

                  <div className="pricing-card-price">
                    {plan.isTrial ? (
                      <>
                        <span className="pricing-worth">Worth {sym}{priceSet.worth.toLocaleString()}</span>
                        <strong className="pricing-free">FREE</strong>
                        <span className="pricing-cycle">{cycleLabel}</span>
                      </>
                    ) : (
                      <>
                        <strong>{sym}{currentPrice.toLocaleString()}</strong>
                        <span className="pricing-cycle">{cycleLabel}</span>
                        {savingsPct > 0 && <span className="pricing-save-inline">Save {savingsPct}%</span>}
                      </>
                    )}
                  </div>

                  <ul className="pricing-card-features">
                    <li><Check size={16} strokeWidth={2.4} /> {plan.credits.toLocaleString()} credits {plan.isTrial ? 'included for trial' : billing === 'annual' ? 'included annually' : 'included monthly'}</li>
                    <li><Check size={16} strokeWidth={2.4} /> {plan.bus} business unit{plan.bus > 1 ? 's' : ''} included</li>
                    <li><Check size={16} strokeWidth={2.4} /> Extra business unit: {sym}{extraBu.toLocaleString()}</li>
                    <li><Check size={16} strokeWidth={2.4} /> Appointments, workforce &amp; customer management</li>
                    <li><Check size={16} strokeWidth={2.4} /> WhatsApp &amp; email automation</li>
                  </ul>

                  <Link to="/contact" className={`btn ${plan.featured ? 'btn-primary' : 'btn-secondary'} pricing-cta`}>
                    {plan.isTrial ? 'Try it free' : `Get started with ${plan.name}`}
                  </Link>
                </div>
              );
            })}
          </Reveal>

          <Reveal className="pricing-footnote">
            <p>Need a custom plan for your industry? <Link to="/contact">Talk to sales</Link> for a bundled quote.</p>
          </Reveal>
        </div>
      </section>

      <section className="section cta-section">
        <div className="container">
          <Reveal className="cta-box card">
            <h2>Still deciding which plan fits?</h2>
            <p>Book a walkthrough and we’ll help you map your team size and usage onto the right tier.</p>
            <div className="hero-actions center">
              <Link to="/contact" className="btn btn-primary">Book a Demo</Link>
              <Link to="/platform" className="btn btn-secondary">See the platform</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
